import { TechemTheme } from '@techem/techem-theme';
import { Button } from '@techem/ui-components';
import { SIZE } from 'baseui/button';
import TitleWithPrevPageLink from 'presentation/components/TitleWithPrevPageLink';
import { WizardSteps, WizardStepsContainer } from 'presentation/components/WizardSteps';
import { DISABLE_FLOW_DEEP_LINKING_PROTECTION } from 'config';
import React, { FormEvent, useEffect } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { setBillingPeriod } from 'frameworks_drivers/redux/reducers/onboardingInformation/onboardingInformationSlice';
import { paths } from 'frameworks_drivers/routes';
import store from 'store';
import { useStyletron } from 'styletron-react';
import { TrackerUtil } from 'tracking/TrackerUtil';
import useBillingPeriod from '../../../../tools/hooks/useBillingPeriod';
import BillingPeriodStartFormControl from './components/BillingPeriodStartFormControl';
import InfoBoxDescription from './components/InfoBoxDescription';
import InfoBoxHeader from './components/InfoBoxHeader';

interface Props {}

const BillingPeriodPage: React.FC<Props> = () => {
  const { t } = useTranslation();
  const [css] = useStyletron();
  const navigate = useNavigate();
  const prevPath = paths.HouseAddress;

  const { billingPeriodSet, selectedIndex, setSelectedIndex } = useBillingPeriod();

  useEffect(() => {
    if (
      !DISABLE_FLOW_DEEP_LINKING_PROTECTION &&
      // Assumption: previous page has to be filled with user input before the user is allowed to visit this page
      store.getState().onboardingInfo.houseAddress.street === ''
    ) {
      navigate(prevPath, { replace: true });
    }
  }, [prevPath, navigate]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    const billingDateStart = billingPeriodSet.getBillingPeriodByIndex(selectedIndex).getStart();
    const billingDateEnd = billingPeriodSet.getBillingPeriodByIndex(selectedIndex).getEnd();

    const billingDateStartUTC = new Date(
      Date.UTC(
        billingDateStart.getFullYear(),
        billingDateStart.getMonth(),
        billingDateStart.getDate()
      )
    );
    const billingDateEndUTC = new Date(
      Date.UTC(billingDateEnd.getFullYear(), billingDateEnd.getMonth(), billingDateEnd.getDate())
    );

    store.dispatch(
      setBillingPeriod({
        startDate: billingDateStartUTC.toISOString(),
        endDate: billingDateEndUTC.toISOString()
      })
    );

    TrackerUtil.trackNextBtnClick();
    navigate(paths.Summary);
  };

  const handlePrevBtnClicked = () => {
    TrackerUtil.trackPrevBtnClick();
    navigate(prevPath);
  };

  return (
    <Container className="pb-4 mb-5">
      <WizardStepsContainer>
        <WizardSteps currentStep={4} totalSteps={4} />
      </WizardStepsContainer>

      <TitleWithPrevPageLink
        title={t('pageBillingPeriodTitle')}
        onPrevClick={handlePrevBtnClicked}
      />

      <form onSubmit={handleSubmit}>
        <Row className="py-4 py-md-2 justify-content-center">
          <Col xs={12} md={10}>
            <Row className="justify-content-center">
              <Col
                xs={12}
                lg={10}
                style={{
                  backgroundColor: TechemTheme.colors.backgroundSecondary
                }}
                className={css({
                  borderTopLeftRadius: '8px',
                  borderTopRightRadius: '8px',
                  borderBottomLeftRadius: '8px',
                  borderBottomRightRadius: '8px'
                })}
              >
                <InfoBoxHeader />
                <InfoBoxDescription />
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col xs={10} lg={8} xl={7} className="mt-2 mt-sm-4">
                <Row>
                  <span
                    className={'text-center fw-bold fs-6 lh-base my-3'}
                    style={{ color: 'rgb(33, 37, 41)' }}
                  >
                    {t('pageBillingPeriodLabelStartDate')}
                  </span>
                  <Col xs={12} sm>
                    <BillingPeriodStartFormControl
                      selectedIndex={selectedIndex}
                      setSelectedIndex={setSelectedIndex}
                      billingPeriodSet={billingPeriodSet}
                    />
                  </Col>

                  <Col xs={12} sm className="d-flex">
                    <Row className="d-flex mb-sm-3 mt-auto" style={{ height: '44px' }}>
                      <Col xs="auto" className="d-flex">
                        <span className="m-auto">{t('pageBillingPeriodLabelBetweenDates')}</span>
                      </Col>

                      <Col xs="auto" className="d-flex px-0">
                        <span className="m-auto">
                          {billingPeriodSet
                            .getBillingPeriodByIndex(selectedIndex)
                            .getEnd()
                            .toLocaleDateString('de-DE', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })}
                        </span>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
        <Row className="justify-content-center py-5">
          <Col xs={'auto'} className="text-center">
            <Button type="submit" size={SIZE.large} additionalTestId="next">
              {t('nextBtnText')}
            </Button>
          </Col>
        </Row>
      </form>
    </Container>
  );
};

export default BillingPeriodPage;
